// Helpers
import { ReactNode } from "react";
// Chart-Components
import BarChart from "../../Chart-Components/BarChart";
import RadarChart from "../../Chart-Components/RadarChart";
import GaugeChart from "../../Chart-Components/GaugeChart";
import AreaChart from "../../Chart-Components/AreaChart";
import BoxChart from "../../Chart-Components/BoxChart";
import BubbleChart from "../../Chart-Components/BubbleChart";
// Types
import {
  ChartNames,
  ChartSetEight,
  ChartSetFive,
  ChartSetFour,
  ChartSetNine,
  ChartSetOne,
  ChartSetSeven,
  ChartSetSix,
  ChartSetTen,
  ChartSetThree,
  ChartSetTwo,
} from "../Types/ChartTypes";
import CandleStickChart from "../../Chart-Components/CandleStickChart";
import DoughnutChart from "../../Chart-Components/DoughnutChart";
import FunnelChart from "../../Chart-Components/FunnelChart";
import HeatmapChart from "../../Chart-Components/HeatmapChart";
import HistogramChart from "../../Chart-Components/HistogramChart";
import LineChart from "../../Chart-Components/LineChart";
import PieChart from "../../Chart-Components/PieChart";
import ScatterChart from "../../Chart-Components/ScatterChart";
import TreemapChart from "../../Chart-Components/TreemapChart";

type ChartDataType =
  | ChartSetOne["AreaChart"]
  | ChartSetOne["BarChart"]
  | ChartSetTwo["BoxChart"]
  | ChartSetEight["BubbleChart"]
  | ChartSetThree["CandleStickChart"]
  | ChartSetFour["DoughnutChart"]
  | ChartSetFive["FunnelChart"]
  | ChartSetSix["GaugeChart"]
  | ChartSetFive["HeatmapChart"]
  | ChartSetSeven["HistogramChart"]
  | ChartSetOne["LineChart"]
  | ChartSetFour["PieChart"]
  | ChartSetNine["ScatterChart"]
  | ChartSetTen["TreeMapChart"];

export class ChartRenderer {
  private chartName: string;
  private chartData: ChartDataType;

  constructor(chartName: string, chartData: ChartDataType) {
    this.chartName = chartName;
    this.chartData = chartData;
  }

  renderChart(dataKey: string, isHorizontal?: boolean): ReactNode {
    // Map chart names to components
    const chartMap: Partial<Record<ChartNames, ReactNode>> = {
      AreaChart: (
        <AreaChart
          chartData={this.chartData as ChartSetOne["AreaChart"]}
          dataKey={dataKey}
        />
      ),
      BarChart: (
        <BarChart
          chartData={this.chartData as ChartSetOne["BarChart"]}
          dataKey={dataKey}
          isHorizontal={isHorizontal}
        />
      ),
      BoxChart: (
        <BoxChart
          chartData={this.chartData as ChartSetTwo["BoxChart"]}
          dataKey={dataKey}
        />
      ),
      BubbleChart: (
        <BubbleChart
          chartData={this.chartData as ChartSetEight["BubbleChart"]}
          dataKey={dataKey}
        />
      ),
      CandleStickChart: (
        <CandleStickChart
          chartData={this.chartData as ChartSetThree["CandleStickChart"]}
          dataKey={dataKey}
        />
      ),
      DoughnutChart: (
        <DoughnutChart
          chartData={this.chartData as ChartSetFour["DoughnutChart"]}
          dataKey={dataKey}
        />
      ),
      FunnelChart: (
        <FunnelChart
          chartData={this.chartData as ChartSetFive["FunnelChart"]}
          dataKey={dataKey}
        />
      ),
      GaugeChart: (
        <GaugeChart
          chartData={this.chartData as ChartSetSix["GaugeChart"]}
          dataKey={dataKey}
        />
      ),
      HeatmapChart: (
        <HeatmapChart
          chartData={this.chartData as ChartSetFive["HeatmapChart"]}
          dataKey={dataKey}
        />
      ),
      HistogramChart: (
        <HistogramChart
          chartData={this.chartData as ChartSetSeven["HistogramChart"]}
          dataKey={dataKey}
        />
      ),
      LineChart: (
        <LineChart
          chartData={this.chartData as ChartSetOne["LineChart"]}
          dataKey={dataKey}
        />
      ),
      PieChart: (
        <PieChart
          chartData={this.chartData as ChartSetFour["PieChart"]}
          dataKey={dataKey}
        />
      ),
      RadarChart: (
        <RadarChart
          chartData={this.chartData as ChartSetEight["RadarChart"]}
          dataKey={dataKey}
        />
      ),
      ScatterChart: (
        <ScatterChart
          chartData={this.chartData as ChartSetNine["ScatterChart"]}
          dataKey={dataKey}
        />
      ),
      TreeMapChart: (
        <TreemapChart
          chartData={this.chartData as ChartSetTen["TreeMapChart"]}
          dataKey={dataKey}
        />
      ),
    };

    // Return the matching chart or a fallback
    return <>{chartMap[this.chartName as ChartNames]}</>;
  }
}
